
import { ChangeDetectionStrategy, Component, EventEmitter, inject, input, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { GTMService } from '@shared/gtm';
import {
  BillingAddressComponent,
  BusinessDomainType,
  CompanyDetails,
  CompanyDetailsFormComponent,
  PersonalDetails,
  PersonalDetailsComponent,
  ResidentialAddress,
} from '@shared/ui-service';
import { CommercialBillingInfo } from '../../models/commercial-billing-info.model';

const CSL_IMPORTS = [PersonalDetailsComponent, CompanyDetailsFormComponent, BillingAddressComponent];

@Component({
  selector: 'csl-billing-form-commercial',
  standalone: true,
  imports: [FormsModule, ...CSL_IMPORTS],
  templateUrl: './billing-form-commercial.component.html',
  styleUrl: './billing-form-commercial.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BillingFormCommercialComponent {
  private readonly gtmService = inject(GTMService);

  domain$$ = input<BusinessDomainType | null>(null);
  isDomainDisabled$$ = input<boolean>(false);
  formStep = input.required<'commercial' | 'dumpster'>();
  @Input({ required: true }) address!: ResidentialAddress;
  @Output() stepValid = new EventEmitter<boolean>();
  @Output() modelChange = new EventEmitter<CommercialBillingInfo | null>();

  private companyDetails: CompanyDetails | null = null;
  private personalDetails: PersonalDetails | null = null;
  private personalAddress: ResidentialAddress | null = null;

  addressChanged(address: ResidentialAddress | null): void {
    this.personalAddress = address;
    this.stepModelChanged();
  }

  onPersonalDetailsChange(personalDetails: PersonalDetails | null): void {
    this.personalDetails = personalDetails;
    this.stepModelChanged();
  }

  onCompanyDetailsChange(companyDetails: CompanyDetails | null): void {
    this.companyDetails = companyDetails;
    this.stepModelChanged();
  }

  trackControlError(controlName: string, error: string): void {
    this.gtmService.formStep3ErrorEvent(this.formStep(), controlName, error);
  }

  trackCompanyDropdown(businessDomain: string | null | undefined): void {
    this.gtmService.dropdownSelectEvent(businessDomain ?? '', 'company_name', this.companyDetails?.companyName ?? '');
  }

  private stepModelChanged(): void {
    const isValid = !!(this.companyDetails && this.personalAddress && this.personalDetails);
    const model = isValid
      ? ({
          companyDetails: this.companyDetails,
          personalAddress: this.personalAddress,
          personalDetails: this.personalDetails,
        } as CommercialBillingInfo)
      : null;

    this.modelChange.emit(model);
  }
}
